import {
  KeyMilestonesTableRow,
  KpiChartConfig,
  KpiChartConstants,
  KpiChartId,
  KpiKeys
} from '@models';
import { featureFlags } from 'environments/environment';

const KPI_KEYS_TO_CHART_TYPES_MAP: Partial<Record<KpiKeys, KpiChartConfig[]>> = {
  active: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barActiveByCountryToTarget', type: 'bar' },
    {
      id: 'toggleActiveByStage',
      type: 'toggleChart'
    },
    { id: 'barActiveByDaysInStage', type: 'bar' }
  ],
  enrollment: [
    { id: 'trendChart', type: 'trendchart' },
    {
      id: 'toggleEnrollmentByCountry',
      type: 'toggleChart'
    },
    { id: 'barEnrollmentByOrganization', type: 'bar' }
  ],
  earlyterm: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barEarlyTermByCountry', type: 'bar' },
    { id: 'barEarlyTermBySite', type: 'bar' },
    {
      id: 'toggleEarlyTermByReason',
      type: 'toggleChart'
    }
  ],
  enrolled: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barEnrolledByCountry', type: 'bar' },
    { id: 'barEnrolledByOrganization', type: 'bar' }
  ],
  subject_screen_fail_rate: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barSubjectScreenFailRateByCountry', type: 'bar' },
    { id: 'barSubjectScreenFailRateBySite', type: 'bar' },
    {
      id: 'toggleSubjectScreenFailRateByReason',
      type: 'toggleChart'
    }
  ],
  deviation_rate_per_subject: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barDeviationRatePerSubjectByCountry', type: 'bar' },
    { id: 'barDeviationRatePerSubjectBySite', type: 'bar' },
    {
      id: 'toggleDeviationRatePerSubjectByReason',
      type: 'toggleChart'
    }
  ],
  days_to_close_site_findings: [{ id: 'trendChart', type: 'trendchart' }],
  clinical_material_kits_shipped: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barClinicalMaterialKitsShippedByCountry', type: 'bar' },
    { id: 'barClinicalMaterialKitsShippedBySite', type: 'bar' }
  ],
  crf_review_completed: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barCrfReviewCompletedByCountry', type: 'bar' },
    { id: 'barCrfReviewCompletedBySite', type: 'bar' }
  ],
  on_time_dispatch_clinical_materials: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barOnTimeDispatchClinicalMaterialsBySite', type: 'bar' }
  ],
  key_milestones_on_track_to_baseline: [
    { id: 'trendChart', type: 'trendchart' },

    { id: 'keyMilestonesTable', type: 'keyMilestonesTable' }
  ],
  days_to_pay_sites: [{ id: 'trendChart', type: 'trendchart' }],
  source_data_verification: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barSdvComplianceByCountry', type: 'bar' },
    { id: 'barSdvComplianceBySite', type: 'bar' }
  ],
  lab_sample_query_rate: [{ id: 'trendChart', type: 'trendchart' }],
  ereq_usage_for_lab_samples: [{ id: 'trendChart', type: 'trendchart' }],
  lab_kits_shipped: [{ id: 'trendChart', type: 'trendchart' }],
  lab_kit_wastage: [{ id: 'trendChart', type: 'trendchart' }]
};

const KPI_CHART_CONSTANTS: Partial<Record<KpiChartId, KpiChartConstants>> = {
  barActiveByCountryToTarget: {
    titleSuffix: 'By country',
    xAxisLabel: 'Activated sites',
    sortValue: 'asc',
    tooltipText: '% of sites activated by country to target',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  toggleActiveByStage: {
    titleSuffix: 'By activation stage',
    tooltipText: '# of sites by activation stage',
    charts: {
      primaryChart: {
        id: 'barActiveByStage',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieActiveByStage',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barActiveByStage: {
    titleSuffix: 'By activation stage',
    xAxisLabel: 'Site count',
    sortValue: 'asc',
    tooltipText: '# of sites by activation stage',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  barActiveByDaysInStage: {
    titleSuffix: 'Days in stage',
    xAxisLabel: 'Days in stage',
    sortValue: 'alphabetical',
    tooltipText: '# of days in stage',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  barEnrolledByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Percent complete',
    sortValue: 'asc',
    tooltipText: '% of enrolled subjects by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barEnrolledByOrganization: {
    titleSuffix: 'By organization',
    xAxisLabel: 'Count of subjects enrolled',
    sortValue: 'asc',
    tooltipText: '# of enrolled subjects by organization',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  pieActiveByStage: {
    titleSuffix: 'By activation stage',
    sortValue: 'asc',
    tooltipText: '% of active subjects by stage',
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  groupedBarEnrollmentByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Site count',
    sortValue: 'desc',
    tooltipText: '# of sites by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: [
      {
        text: 'Non-enrolling sites',
        color: '#25a1be'
      },
      {
        text: 'Total activated sites',
        color: '#005DAA'
      }
    ]
  },

  barEnrollmentByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Percent complete',
    sortValue: 'desc',
    tooltipText: '% of sites by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  toggleEnrollmentByCountry: {
    titleSuffix: 'By country',
    tooltipText: '# of sites by country',
    charts: {
      primaryChart: {
        id: 'groupedBarEnrollmentByCountry',
        type: 'groupedBar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'barEnrollmentByCountry',
        type: 'bar',
        label: 'Percent'
      }
    }
  },

  barEnrollmentByOrganization: {
    titleSuffix: 'By days since activation',
    xAxisLabel: 'Days since activation',
    sortValue: 'desc',
    tooltipText: '# of days since activation by organization',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barSubjectScreenFailRateByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Screen fail rate',
    sortValue: 'desc',
    tooltipText: 'Screen fail rate by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barSubjectScreenFailRateBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Screen fail rate',
    sortValue: 'desc',
    tooltipText: 'Screen fail rate by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for subject screen failures',
    charts: {
      primaryChart: {
        id: 'barSubjectScreenFailRateByReason',
        type: 'bar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'pieSubjectScreenFailRateByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Reason',
    sortValue: 'desc',
    tooltipText: '# Subject screen failures by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },

  pieSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of subject screen failures by reason',
    fieldFilter: '',
    legendValues: []
  },

  barEarlyTermByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Early terminations',
    sortValue: 'desc',
    tooltipText: 'Early terminations by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barEarlyTermBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Early terminations',
    sortValue: 'desc',
    tooltipText: 'Early terminations by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleEarlyTermByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for early terminations',
    charts: {
      primaryChart: {
        id: 'barEarlyTermByReason',
        type: 'bar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'pieEarlyTermByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barEarlyTermByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Reason',
    sortValue: 'desc',
    tooltipText: '% early terminations by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },

  pieEarlyTermByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of early terminations by reason',
    fieldFilter: '',
    legendValues: []
  },

  barDeviationRatePerSubjectByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Deviation rate',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barDeviationRatePerSubjectBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Deviation rate',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for deviations',
    charts: {
      primaryChart: {
        id: 'barDeviationRatePerSubjectByReason',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieDeviationRatePerSubjectByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Deviation count',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },

  pieDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of deviation rate by reason',
    fieldFilter: '',
    legendValues: []
  },

  barClinicalMaterialKitsShippedByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Clinical material kits shipped by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barClinicalMaterialKitsShippedBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Clinical material kits shipped by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barCrfReviewCompletedByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'CRFs reviewed',
    sortValue: 'desc',
    tooltipText: 'CRF review completion by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barCrfReviewCompletedBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'CRFs reviewed',
    sortValue: 'desc',
    tooltipText: 'CRF review completion by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barOnTimeDispatchClinicalMaterialsBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'On-time dispatch',
    sortValue: 'desc',
    tooltipText: 'On-time dispatch by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barSdvComplianceByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'SDV compliance (%)',
    sortValue: 'desc',
    tooltipText: 'Source data verification (SDV) compliance by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barSdvComplianceBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'SDV compliance (%)',
    sortValue: 'desc',
    tooltipText: 'Source data verification (SDV) compliance by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  }
};

const KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP: Record<keyof KeyMilestonesTableRow, string> = {
  milestoneName: 'Milestone name',
  baselineDate: 'Baseline date',
  plannedDate: 'Planned date',
  actualDate: 'Actual date',
  baselineVariance: 'Baseline variance',
  status: 'Health'
};

const KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID: KpiChartId[] = [
  'barEnrolledByCountry',
  'barEnrolledByOrganization',
  'barActiveByCountryToTarget',
  'barActiveByStage',
  'barActiveByDaysInStage',
  'barEnrollmentByOrganization',
  'barEnrollmentByCountry',
  'groupedBarEnrollmentByCountry',
  'barSubjectScreenFailRateByCountry',
  'barSubjectScreenFailRateBySite',
  'barSubjectScreenFailRateByReason',
  'barEarlyTermByCountry',
  'barEarlyTermBySite',
  'barEarlyTermByReason',
  'barDeviationRatePerSubjectByCountry',
  'barDeviationRatePerSubjectBySite',
  'barDeviationRatePerSubjectByReason',
  'barClinicalMaterialKitsShippedByCountry',
  'barClinicalMaterialKitsShippedBySite',
  'barCrfReviewCompletedByCountry',
  'barCrfReviewCompletedBySite',
  'barOnTimeDispatchClinicalMaterialsBySite',
  'barSdvComplianceByCountry',
  'barSdvComplianceBySite'
];

const KPI_EXCLUDES_ON_TRACK_IN_COUNT: KpiKeys[] = ['subject_screen_fail_rate'];

const KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP: Record<string, string> = {
  study_deviation: 'Study Deviation',
  recovery_from_indication: 'Recovery from Indication',
  death: 'Death',
  progressive_disease: 'Progressive Disease',
  lack_of_efficacy: 'Lack of Efficacy',
  inclusion_criteria: 'Inclusion Criteria',
  terminated_by_sponsor: 'Terminated by Sponsor',
  adverse_event: 'Adverse Event',
  non_compliance: 'Non-Compliance',
  physician_decision: 'Physician Decision',
  withdrawn_by_subject: 'Withdrawn by Subject',
  pregnancy: 'Pregnancy',
  lost_to_follow_up: 'Lost to Follow Up',
  unknown: 'Unknown',
  exclusion_criteria: 'Exclusion Criteria'
};

const KPI_EARLY_TERM_REASON_MAP: Record<string, string> = {
  adverse_event: 'Adverse Event',
  death: 'Death',
  exclusion_criteria: 'Exclusion Criteria',
  inclusion_criteria: 'Inclusion Criteria',
  lack_of_efficacy: 'Lack of Efficacy',
  lost_to_follow_up: 'Lost to Follow Up',
  non_compliance: 'Non-Compliance',
  physician: 'Physician',
  decision: 'Decision',
  pregnancy: 'Pregnancy',
  progressive_disease: 'Progressive Disease',
  recovery_from_indication: 'Recovery from Indication',
  study_deviation: 'Study Deviation',
  terminated_by_sponsor: 'Terminated by Sponsor',
  unknown: 'Unknown',
  withdrawn_by_subject: 'Withdrawn by Subject'
};

const KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP: Record<string, string> = {
  study_intervention: 'Study Intervention',
  ich_gcp_deviation: 'ICH/GCP Deviation',
  ich_gcp_issue: 'ICH/GCP Issue',
  protocol_deviation: 'Protocol Deviation',
  informed_consent: 'Informed Consent',
  discontinuation: 'Discontinuation',
  inclusion_exclusion: 'Inclusion/Exclusion',
  trial_procedures: 'Trial Procedures',
  safety_reporting: 'Safety Reporting',
  prohibited_medication_or_treatment: 'Prohibited Medication or Treatment'
};

// Helper function to filter out hidden KPIs from chart types map
function filterHiddenKpisFromChartMap(
  chartMap: Partial<Record<KpiKeys, KpiChartConfig[]>>
): Partial<Record<KpiKeys, KpiChartConfig[]>> {
  const filtered = { ...chartMap };

  // Remove entire KPI entries for hiddenKPIs
  featureFlags.hiddenKPIs.forEach((hiddenKey) => {
    delete filtered[hiddenKey as KpiKeys];
  });

  // For hiddenDrilldownCharts, keep trend chart but remove all other chart types
  featureFlags.hiddenDrilldownCharts.forEach((hiddenKey) => {
    const kpiKey = hiddenKey as KpiKeys;
    if (filtered[kpiKey]) {
      // Keep only the trend chart, filter out all other chart types (bar, pie, toggle, etc.)
      filtered[kpiKey] = filtered[kpiKey]!.filter(
        (chartConfig) => chartConfig.type === 'trendchart'
      );
    }
  });

  return filtered;
}

// Apply filtering to KPI_KEYS_TO_CHART_TYPES_MAP
const FILTERED_KPI_KEYS_TO_CHART_TYPES_MAP = filterHiddenKpisFromChartMap(
  KPI_KEYS_TO_CHART_TYPES_MAP
);

export {
  KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP,
  KPI_CHART_CONSTANTS,
  KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID,
  KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
  KPI_EARLY_TERM_REASON_MAP,
  KPI_EXCLUDES_ON_TRACK_IN_COUNT,
  FILTERED_KPI_KEYS_TO_CHART_TYPES_MAP as KPI_KEYS_TO_CHART_TYPES_MAP,
  KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP
};
